import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixPaymentsPayinsConfigurationsV1PayinsConfigurationServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {};

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_payments_payins-configurations';

/** Creates a PayinsConfiguration. */
export function createPayinsConfiguration(
  payload: object
): RequestOptionsFactory<any> {
  function __createPayinsConfiguration({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'payinsConfiguration.createdDate' },
          { path: 'payinsConfiguration.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.payments.payins_configurations.v1.payins_configuration',
      method: 'POST' as any,
      methodFqn:
        'wix.payments.payins_configurations.v1.PayinsConfigurationService.CreatePayinsConfiguration',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsPayinsConfigurationsV1PayinsConfigurationServiceUrl(
        { protoPath: '/v1/payins-configurations', data: serializedData, host }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'payinsConfiguration.createdDate' },
              { path: 'payinsConfiguration.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createPayinsConfiguration;
}

/** Retrieves a PayinsConfiguration. */
export function getPayinsConfiguration(
  payload: object
): RequestOptionsFactory<any> {
  function __getPayinsConfiguration({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.payments.payins_configurations.v1.payins_configuration',
      method: 'GET' as any,
      methodFqn:
        'wix.payments.payins_configurations.v1.PayinsConfigurationService.GetPayinsConfiguration',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsPayinsConfigurationsV1PayinsConfigurationServiceUrl(
        {
          protoPath: '/v1/payins-configurations/{payinsConfigurationId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'payinsConfiguration.createdDate' },
              { path: 'payinsConfiguration.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getPayinsConfiguration;
}

/**
 * Updates a PayinsConfiguration.
 *
 * Each time the PayinsConfiguration is updated,
 * `revision` increments by 1.
 * The current `revision` must be passed when updating the PayinsConfiguration.
 * This ensures you're working with the latest PayinsConfiguration
 * and prevents unintended overwrites.
 */
export function updatePayinsConfiguration(
  payload: object
): RequestOptionsFactory<any> {
  function __updatePayinsConfiguration({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'payinsConfiguration.createdDate' },
          { path: 'payinsConfiguration.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.payments.payins_configurations.v1.payins_configuration',
      method: 'PATCH' as any,
      methodFqn:
        'wix.payments.payins_configurations.v1.PayinsConfigurationService.UpdatePayinsConfiguration',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsPayinsConfigurationsV1PayinsConfigurationServiceUrl(
        {
          protoPath: '/v1/payins-configurations/{payinsConfiguration.id}',
          data: serializedData,
          host,
        }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'payinsConfiguration.createdDate' },
              { path: 'payinsConfiguration.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updatePayinsConfiguration;
}

/**
 * Retrieves a list of PayinsConfigurations, given the provided [paging, filtering, and sorting][1].
 *
 * Up to 1,000 PayinsConfigurations can be returned per request.
 *
 * To learn how to query PayinsConfigurations, see [API Query Language][2].
 *
 * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
 * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
 */
export function queryPayinsConfigurations(
  payload: object
): RequestOptionsFactory<any> {
  function __queryPayinsConfigurations({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.payments.payins_configurations.v1.payins_configuration',
      method: 'GET' as any,
      methodFqn:
        'wix.payments.payins_configurations.v1.PayinsConfigurationService.QueryPayinsConfigurations',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsPayinsConfigurationsV1PayinsConfigurationServiceUrl(
        { protoPath: '/v1/payins-configurations/query', data: payload, host }
      ),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'payinsConfigurations.createdDate' },
              { path: 'payinsConfigurations.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixPaymentsPayinsConfigurationsV1PayinsConfigurationServiceUrl(
            {
              protoPath: '/v1/payins-configurations/query',
              data: payload,
              host,
            }
          ),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryPayinsConfigurations;
}

/**
 * Synchronously update tags on multiple PayinsConfigurations, by list of PayinsConfigurations ids
 * A tag that appears both in the list of assign and unassign tags, will be assigned
 */
export function bulkUpdatePayinsConfigurationTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdatePayinsConfigurationTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.payments.payins_configurations.v1.payins_configuration',
      method: 'POST' as any,
      methodFqn:
        'wix.payments.payins_configurations.v1.PayinsConfigurationService.BulkUpdatePayinsConfigurationTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsPayinsConfigurationsV1PayinsConfigurationServiceUrl(
        {
          protoPath: '/v1/bulk/payins-configurations/update-tags',
          data: payload,
          host,
        }
      ),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdatePayinsConfigurationTags;
}

/**
 * Asynchronously update tags on multiple PayinsConfigurations, by provided filter
 * An empty filter will update all PayinsConfigurations
 * A tag that appears both in the list of assign and unassign tags, will be assigned
 */
export function bulkUpdatePayinsConfigurationTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdatePayinsConfigurationTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.payments.payins_configurations.v1.payins_configuration',
      method: 'POST' as any,
      methodFqn:
        'wix.payments.payins_configurations.v1.PayinsConfigurationService.BulkUpdatePayinsConfigurationTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsPayinsConfigurationsV1PayinsConfigurationServiceUrl(
        {
          protoPath: '/v1/bulk/payins-configurations/update-tags-by-filter',
          data: payload,
          host,
        }
      ),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdatePayinsConfigurationTagsByFilter;
}
